// Navigation configuration for all projects
// Centralized configuration for sidebar navigation items

import {
	BillingIcon,
	HelpCenterIcon,
} from '../components/MainNavigation/icons'

// Import project-specific navigation items
import {
	getLTaxNavigationItems,
	getLEPRNavigationItems,
	getLInvoiceNavigationItems,
	getLAuthorizeNavigationItems,
	getBASICNavigationItems,
} from './navigation'
import {
	BILLING_HISTORY_PATH,
	isBillingSidebarActive,
} from '../routes/shared/coreRoutes'

/**
 * Navigation item configuration
 * @typedef {Object} NavigationItem
 * @property {string} url - Route URL
 * @property {string} label - Menu label (will be uppercased)
 * @property {Function} icon - Icon component function
 * @property {boolean} selected - Whether item is selected
 * @property {number|null} badge - Badge count (from newMessagesCount)
 * @property {boolean} isTaxAgentMainVisible - Visible for tax agent/manager
 * @property {string} badgeKey - Key in newMessagesCount object
 * @property {Function} filterCondition - Custom filter function
 */

/**
 * Get navigation items for a specific project
 * @param {string} projectName - Project name (LTax, LEPR, LInvoice, LAuthorize, etc.)
 * @param {Object} context - Context object with location, t, newMessagesCount, globalState, isTaxAgentMain
 * @returns {Array<NavigationItem>} Array of navigation items
 */
export const getNavigationItems = (projectName, context) => {
	// Normalize project name: OMP/LOmp is the same as LAuthorize
	if (projectName === 'LOmp' || projectName === 'OMP' || projectName === 'LOMP') {
		projectName = 'LAuthorize'
	}

	// Map project names to their navigation item getters
	const navigationGetters = {
		LTax: getLTaxNavigationItems,
		LEPR: getLEPRNavigationItems,
		LInvoice: getLInvoiceNavigationItems,
		LAuthorize: getLAuthorizeNavigationItems,
		BASIC: getBASICNavigationItems,
	}

	// Get navigation items for the project
	const getNavigationItemsForProject = navigationGetters[projectName] || navigationGetters.LTax
	let items = getNavigationItemsForProject(context)

	// Apply filters
	const { isTaxAgentMain } = context
	items = items.filter((item) => {
		// Custom filter condition
		if (item.filterCondition && !item.filterCondition()) {
			return false
		}

		// Tax agent visibility filter
		if (isTaxAgentMain && item.isTaxAgentMainVisible === false) {
			return false
		}

		// Hide clients for non-tax-agent
		if (!isTaxAgentMain && item.url === '/clients') {
			return false
		}

		return true
	})

	// Transform items for Polaris Navigation
	return items.map((item) => ({
		url: item.url,
		label: item.label.toUpperCase(),
		icon: item.icon,
		badge: item.badge,
		selected: item.selected,
	}))
}

/**
 * Get bottom navigation items (billing, help) - common for all projects
 * @param {Object} context - Context object with location, t, newMessagesCount
 * @returns {Array<NavigationItem>} Array of bottom navigation items
 */
export const getBottomNavigationItems = (context) => {
	const {
		location,
		t,
		newMessagesCount = {}
	} = context

	const printMessagesCount = (count) => {
		if (count) {
			return count
		}
		return null
	}

	const billingActive = isBillingSidebarActive(location.pathname)

	return [
		{
			url: BILLING_HISTORY_PATH,
			label: t('menu.billing')
					.toUpperCase(),
			icon: () => <BillingIcon selected={billingActive} />,
			badge: printMessagesCount(newMessagesCount['billing history']),
			selected: billingActive,
		},
		{
			url: '/help',
			label: t('menu.helpCenter')
					.toUpperCase(),
			icon: () => <HelpCenterIcon selected={location.pathname.startsWith('/help')} />,
			badge: printMessagesCount(newMessagesCount.support),
			selected: location.pathname.startsWith('/help'),
		},
	]
}

/**
 * Get project title for sidebar header
 * @param {string} projectName - Project name
 * @param {Object} user - User object (for OMP check)
 * @returns {string} Project title
 */
export const getProjectTitle = (projectName, user) => {
	// Normalize project name: OMP/LOmp is the same as LAuthorize
	if (projectName === 'LOmp' || projectName === 'OMP' || projectName === 'LOMP') {
		projectName = 'LAuthorize'
	}

	const titles = {
		LTax: user?.company?.is_omp ? "L'marketplace" : "L'tax",
		LEPR: "L'EPR",
		LInvoice: "L'invoice",
		LAuthorize: "L'Authorize",
		BASIC: 'Basic',
	}

	return titles[projectName] || titles.LTax
}
